/**
 * H22: sect activities (ลาดตระเวนสำนัก, กำจัดผู้มาสอดแนม, สอดแนมสำนักอื่น) and manual pages (หน้าชิ้นส่วนตำรา).
 * Data only; rules in systems/sectActivities.ts. Placeholder text and numbers (docs/DECISIONS_2026-10-11.md).
 * A book is a sect art (`art:<id>`) or one of its three neigong (`ng:<id>`); its pages are goods `page:<bookId>:<n>`
 * (n = 1..pageCount), the bound book is the good `book:<bookId>`.
 */
import { SECT_NEIGONG, NEIGONG } from './neigong';
import { SECT_TABLE } from './layouts/sects';
import type { GoodDef } from './professions';

export const PAGE_COUNT = 10;
export const pageId = (book: string, n: number): string => `page:${book}:${n}`;
export const bookGoodId = (book: string): string => `book:${book}`;
/** Parses `page:<bookId>:<n>` (bookId may hold colons). */
export function parsePageId(id: string): { book: string; n: number } | null {
  const m = /^page:(.+):(\d+)$/.exec(id);
  return m ? { book: m[1]!, n: Number(m[2]) } : null;
}

export interface SectBook {
  readonly id: string;
  readonly sect: string;
  readonly kind: 'art' | 'neigong';
  readonly target: string;
  readonly name: string;
  readonly pageCount: number;
}

/** Each sect's arts (static: data/skills.ts cannot be imported here without a cycle through professions.ts). */
const SECT_ARTS: Readonly<Record<string, readonly string[]>> = {
  'bamboo-tide': ['tide-staff'],
  'mist-peak': ['breeze-sword', 'drift-fist', 'drift-sword'],
  'bell-stone': ['hum-palm', 'founder-sabre'],
  'lantern-road': ['chain-hold', 'quick-draw-sabre'],
  'dim-moon': ['listening-blade', 'chorus'],
  'pine-ink': ['pulse-seal', 'one-cut'],
  'cinnabar': ['tongs-claw', 'cliff-push-sabre'],
  'snow-pine': ['snow-sword'],
};

/** Every sect's books: its arts and its neigong. */
export const SECT_BOOKS: readonly SectBook[] = SECT_TABLE.flatMap((s): SectBook[] => [
  ...(SECT_ARTS[s.id] ?? []).map(
    (a): SectBook => ({ id: `art:${a}`, sect: s.id, kind: 'art', target: a, name: `ตำราวิชา ${a}`, pageCount: PAGE_COUNT }),
  ),
  ...(SECT_NEIGONG[s.id] ?? []).filter((n) => NEIGONG[n]).map(
    (n): SectBook => ({ id: `ng:${n}`, sect: s.id, kind: 'neigong', target: n, name: `คัมภีร์${NEIGONG[n]!.name}`, pageCount: PAGE_COUNT }),
  ),
]);
export const bookById = (id: string): SectBook | undefined => SECT_BOOKS.find((b) => b.id === id);
export const booksOfSect = (sect: string): readonly SectBook[] => SECT_BOOKS.filter((b) => b.sect === sect);

export const SECT_ACT = {
  monster: 'bandit',
  daily: { patrol: 3, hunt: 3, spy: 2 },
  /** Absolute sect-map points (data/layouts/sects.ts: hall door 48,41; training yard centre 24,67; 2/3 toward the edges). */
  patrolPoints: [
    { x: 48, y: 44 },
    { x: 24, y: 67 },
    { x: 16, y: 48 },
    { x: 80, y: 48 },
  ],
  reach: 2.5,
  intruderChance: 0.5,
  huntSpies: 3,
  huntSpots: [
    { x: 24, y: 62 },
    { x: 72, y: 66 },
    { x: 20, y: 46 },
  ],
  intruderSpot: { x: 24, y: 67 },
  spyPoints: [
    { x: 48, y: 44 },
    { x: 24, y: 67 },
    { x: 80, y: 48 },
  ],
  guards: [
    { x: 32, y: 60 },
    { x: 74, y: 52 },
  ],
  detectRadius: 4,
  pages: { patrol: 2, hunt: 1, spy: 3 },
} as const;

export type SectActKind = 'patrol' | 'hunt' | 'spy';
export const SECT_ACT_NAME: Readonly<Record<SectActKind, string>> = {
  patrol: 'ลาดตระเวนสำนัก',
  hunt: 'กำจัดผู้มาสอดแนม',
  spy: 'สอดแนมสำนักอื่น',
};

export const SECT_PAGE_GOODS: readonly GoodDef[] = SECT_BOOKS.flatMap((b): GoodDef[] => [
  ...Array.from({ length: b.pageCount }, (_, i): GoodDef => ({
    id: pageId(b.id, i + 1),
    name: `หน้าตำรา${b.name} ${i + 1}`,
    kind: 'material',
    desc: `รวมให้ครบ ${b.pageCount} หน้าเพื่อเย็บเป็น${b.name}`,
    price: 80,
  })),
  { id: bookGoodId(b.id), name: b.name, kind: 'material', desc: 'อ่านเพื่อเรียนตามทางเรียนปกติ', price: 800 },
]);
